import { useState, useMemo } from 'react';
import { isStateChanged, convertUnit, getPermissionDeniedToastMessage, validateName, getAirUnit } from '../../../../utils/utility';
import { MODES, ENERGY_MODES } from '../../../../utils/constant';
import HCServices from '../../../../services/hcServices.js';
import I18n from '../../../../localization';
import _ from 'lodash';

export const useIQAirSettings = ({ iqAirDetails, setIqAirDetails, IQAirPoints, selectedValue, range, availableModes, hasAccess, maskedOwnerEmail, siteId, iqAirId, setToast, setHeaderName, loading }) => {
  const [savingLoader, setSavingLoader] = useState(false);
  const [tempIQAirDetails, setTempIqAirDetails] = useState({});
  const [tempIQAirPoints, setTempIQAirPoints] = useState({});
  const [tempSelectedValue, setTempSelectedValue] = useState(null);
  const [tempRange, setTempRangeInternal] = useState(null);
  const [isRefreshed, setIsRefreshedState] = useState(false);
  const [IQAirNameError, setIQAirNameError] = useState({ status: false, message: '' });

  const isPageDataChanged = useMemo(() => {
    if (isRefreshed) {
      setTempIQAirPoints(IQAirPoints);
      setTempIqAirDetails(iqAirDetails);
      setTempRangeInternal(range);
      setTempSelectedValue(selectedValue);
      setIsRefreshedState(false);
      return false;
    }
    
    // Add loading check like original code
    if (loading) {
      return false;
    }
    
    return (
      !_.isEqual(iqAirDetails, tempIQAirDetails) ||
      !_.isEqual(IQAirPoints, tempIQAirPoints) || 
      !_.isEqual(range, tempRange) || 
      !_.isEqual(selectedValue, tempSelectedValue)
    );
  }, [iqAirDetails, tempIQAirDetails, tempIQAirPoints, IQAirPoints, isRefreshed, selectedValue, range, tempRange, tempSelectedValue, loading]);

  const handleNameChange = (e) => {
    const value = e.target.value;
    setIqAirDetails((prev) => ({
      ...prev,
      name: value
    }));
    const status = validateName(value);
    if (status !== true) {
      setIQAirNameError({ status: true, message: status });
    } else {
      setIQAirNameError({ status: false, message: '' });
    }
  };

  const setIQAirSettings = async () => {
    if (!hasAccess) {
      setToast({
        status: true,
        message: getPermissionDeniedToastMessage(maskedOwnerEmail),
        type: 'error',
        isPermissionDeniedMsg: true
      });
      return false;
    }

    const payload = {};
    const hasHeatSupport = Array.isArray(availableModes) && availableModes.includes(MODES.HEAT);
    const hasCoolSupport = Array.isArray(availableModes) && availableModes.includes(MODES.COOL);
    
    // Handle name and mode changes
    for (const [key, value] of Object.entries({
      "name": "name",
      "temperature-mode": "airMode",
      "fan-mode": "fanMode"
    })) {
      if (isStateChanged(iqAirDetails[value], tempIQAirDetails[value])) {
        payload[key] = iqAirDetails[value];
      }
    }

    // Handle setpoint changes
    for (const [key, value] of Object.entries({
      "heat-setpoint": "heat_setpoint",
      "cool-setpoint": "cool_setpoint"
    })) {
      if ((key === "heat-setpoint" && hasHeatSupport) || 
          (key === "cool-setpoint" && hasCoolSupport)) {
        if (isStateChanged(IQAirPoints[value], tempIQAirPoints[value])) {
          payload[key] = convertUnit(parseFloat(IQAirPoints[value]), 'FTOC');
        }
      }
    }

    // Handle energy mode changes
    if (isStateChanged(tempSelectedValue, selectedValue)) {
      let initialEnergyMode = ''
      if(selectedValue.local === ENERGY_MODES.SMART){
        initialEnergyMode = 'SMART'
      }else if(selectedValue.local === ENERGY_MODES.MANUAL){
        initialEnergyMode = 'MANUAL'
      }else if(selectedValue.local === ENERGY_MODES.SCHEDULE){
        initialEnergyMode = 'SCHEDULE'
      }
      payload["energy-mode"] = initialEnergyMode;
    }

    // Handle comfort range changes for smart mode
    if (selectedValue.local === ENERGY_MODES.SMART && isStateChanged(range, tempRange)) {
      const rangeDividend = getAirUnit() === "F" ? (1/2) : (1);
      const shouldMirrorAutoComfortRange = hasHeatSupport && hasCoolSupport;
      
      if (hasHeatSupport && isStateChanged(range?.heat_setpoint, tempRange?.heat_setpoint)) {
        const computedLower = (IQAirPoints.heat_setpoint - range.heat_setpoint[0]) * rangeDividend;
        const computedUpper = (range.heat_setpoint[1] - IQAirPoints.heat_setpoint) * rangeDividend;

        payload["heat-comfort-range"] = {
          lower: computedLower,
          upper: shouldMirrorAutoComfortRange ? computedLower : computedUpper,
        };
      }
      
      if (hasCoolSupport && isStateChanged(range?.cool_setpoint, tempRange?.cool_setpoint)) {
        const computedLower = (IQAirPoints.cool_setpoint - range.cool_setpoint[0]) * rangeDividend;
        const computedUpper = (range.cool_setpoint[1] - IQAirPoints.cool_setpoint) * rangeDividend;

        payload["cool-comfort-range"] = {
          lower: shouldMirrorAutoComfortRange ? computedUpper : computedLower,
          upper: computedUpper,
        };
      }
    }

    try {
      setSavingLoader(true);
      const res = await HCServices.setAirDeviceSettings(payload, siteId, iqAirId);
      
      if (res.data.status === 'ok') {
        if (isStateChanged(tempIQAirDetails.name, iqAirDetails.name)) {
          setHeaderName(iqAirDetails.name);
          // Update local storage function would need to be imported
          // updateIQAirNameInLocalStorage(iqAirId, iqAirDetails.name);
        }
        setToast({
          status: true,
          message: I18n.t('mobile.iqAirSetting.messages.success'),
          type: 'success'
        });
        setIsRefreshedState(true);
        return true;
      } else {
        throw new Error('Unable to change Settings.');
      }
    } catch (error) {
      setToast({
        status: true,
        message: I18n.t('mobile.iqAirSetting.messages.error'),
        type: 'error'
      });
      return false;
    } finally {
      setSavingLoader(false);
    }
  };

  return {
    savingLoader,
    tempIQAirDetails,
    tempIQAirPoints,
    tempSelectedValue,
    tempRange,
    isRefreshed,
    IQAirNameError,
    isPageDataChanged,
    handleNameChange,
    setIQAirSettings,
    setTempIqAirDetails,
    setTempIQAirPoints,
    setTempSelectedValue,
    setTempRange: setTempRangeInternal,
    setIsRefreshed: setIsRefreshedState
  };
};
